import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import LogicContext from "../../Context/LogicContext";
import { departmentService } from "../../services/department.service";

function DesignationFormModal({ mode, initialValues, onSubmit, onClose }) {
  const logicContext = useContext(LogicContext);
  const logicContextRef = useRef(logicContext);
  useEffect(() => {
    logicContextRef.current = logicContext;
  }, [logicContext]);

  const [name, setName] = useState(initialValues?.name || "");
  const [statusId, setStatusId] = useState(
    String(Number(initialValues?.statusId) === 1 ? 1 : 2)
  );
  const [departmentId, setDepartmentId] = useState(
    initialValues?.departmentId == null || initialValues.departmentId === "-"
      ? ""
      : String(initialValues.departmentId)
  );
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isEdit = mode === "edit";

  useEffect(() => {
    setName(initialValues?.name || "");
    setStatusId(String(Number(initialValues?.statusId) === 1 ? 1 : 2));
    setDepartmentId(
      initialValues?.departmentId == null || initialValues.departmentId === "-"
        ? ""
        : String(initialValues.departmentId)
    );
    setError("");
  }, [
    initialValues?.name,
    initialValues?.statusId,
    initialValues?.departmentId,
  ]);

  useEffect(() => {
    let isMounted = true;
    setDepartmentsLoading(true);
    departmentService
      .getDepartments()
      .then((result) => {
        if (isMounted) {
          setDepartments(
            result.departments.filter(
              (department) => Number(department.statusId) === 1
            )
          );
        }
      })
      .catch((loadError) => {
        if (isMounted) {
          const message =
            loadError?.response?.data?.msg ||
            loadError?.message ||
            "Unable to load departments.";
          setError(message);
          logicContextRef.current.showToast(message, "error");
        }
      })
      .finally(() => {
        if (isMounted) setDepartmentsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const departmentOptions = useMemo(() => {
    if (
      !isEdit ||
      !departmentId ||
      departments.some((department) => String(department.id) === departmentId)
    ) {
      return departments;
    }

    return [
      ...departments,
      {
        id: departmentId,
        name: initialValues?.departmentName || `Department ${departmentId}`,
        statusId: initialValues?.departmentStatusId,
        isInactive: true,
      },
    ];
  }, [
    departments,
    departmentId,
    initialValues?.departmentName,
    initialValues?.departmentStatusId,
    isEdit,
  ]);

  async function handleSubmit(event) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Designation name is required.");
      return;
    }
    if (trimmedName.length > 100) {
      setError("Designation name must be 100 characters or fewer.");
      return;
    }
    if (!departmentId) {
      setError("Department is required.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit({
        name: trimmedName,
        departmentId: Number(departmentId),
        ...(isEdit ? { statusId: Number(statusId) } : {}),
      });
      onClose();
    } catch (submitError) {
      setError(submitError?.message || "Unable to save designation.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!submitting) onClose();
      }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="designation-form-title"
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle id="designation-form-title">
          {isEdit ? "Edit Designation" : "Add Designation"}
        </DialogTitle>
        <DialogContent className="flex flex-col gap-4 pt-2">
          <TextField
            autoFocus
            required
            fullWidth
            label="Designation Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 100 }}
            disabled={submitting}
            error={Boolean(error) && !name.trim()}
          />
          <TextField
            select
            required
            fullWidth
            label="Department"
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
            disabled={submitting || departmentsLoading}
            error={Boolean(error) && !departmentId}
          >
            {departmentOptions.map((department) => (
              <MenuItem key={department.id} value={String(department.id)}>
                {department.name}
                {department.isInactive ? " (Inactive)" : ""}
              </MenuItem>
            ))}
          </TextField>
          {isEdit ? (
            <TextField
              select
              fullWidth
              label="Status"
              value={statusId}
              onChange={(event) => setStatusId(event.target.value)}
              disabled={submitting}
            >
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="2">Inactive</MenuItem>
            </TextField>
          ) : null}
          {error ? (
            <div role="alert" className="text-sm text-red-600">
              {error}
            </div>
          ) : null}
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={onClose} disabled={submitting}>
            Cancel Designation
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save Designation" : "Create Designation"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default DesignationFormModal;